/* Kassandra PDP — free-shipping progress bar + charm add-on (draft theme). */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

function rate() {
  const c = window.Shopify && Shopify.currency;
  const r = c && parseFloat(c.rate);
  return r && isFinite(r) ? r : 1;
}

function fmt(cents, currency) {
  const value = Math.max(0, Math.round(cents)) / 100;
  const whole = Math.round(value * 100) % 100 === 0;
  const num = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
  if (!currency || currency === 'EUR') return `€${num}`;
  try {
    const sym = new Intl.NumberFormat('it-IT', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return `${sym ? sym.value : currency + ' '}${num}`;
  } catch {
    return `${currency} ${num}`;
  }
}

function render(bar, cart) {
  const shopCurrency = bar.dataset.shopCurrency || 'EUR';
  const currency = (cart && cart.currency) || shopCurrency;
  const base = parseInt(bar.dataset.threshold, 10) || 4500;
  const threshold = currency === shopCurrency ? base : Math.round(base * rate());
  const total = cart ? Number(cart.total_price) || 0 : 0;
  const item = parseInt(bar.dataset.itemPrice || '0', 10) || 0;
  const msg = bar.querySelector('[data-kc-fsb-msg]');
  const fill = bar.querySelector('[data-kc-fsb-fill]');
  const track = bar.querySelector('[data-kc-fsb-track]');
  let pct = 0;
  let state = 'idle';
  if (total > 0 && total >= threshold) {
    state = 'reached';
    pct = 100;
    msg.textContent = 'Hai diritto alla spedizione gratuita';
  } else if (total > 0) {
    state = 'progress';
    pct = Math.min(100, Math.max(2, (total / threshold) * 100));
    msg.innerHTML = '';
    msg.append('Ti mancano ');
    const s = document.createElement('strong');
    s.textContent = fmt(threshold - total, currency);
    msg.append(s, ' per la spedizione gratuita');
  } else if (item > 0 && item >= threshold) {
    state = 'item-reached';
    pct = 100;
    msg.textContent = 'Con questo articolo la spedizione è gratuita';
  } else if (item > 0) {
    // step26: PDP with empty cart — remaining amount if the customer buys this item
    state = 'item';
    pct = Math.min(100, Math.max(2, (item / threshold) * 100));
    msg.innerHTML = '';
    msg.append('Con questo articolo ti mancano ');
    const s = document.createElement('strong');
    s.textContent = fmt(threshold - item, currency);
    msg.append(s, ' per la spedizione gratuita');
  } else {
    msg.textContent = base === 4500 ? 'Spedizione gratuita in Italia su tutte le borse' : `Spedizione gratuita per ordini da ${fmt(threshold, currency)}`;
  }
  bar.dataset.state = state;
  if (fill) fill.style.width = `${pct}%`;
  if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));
}

let inflight = null;
async function refresh() {
  const bars = document.querySelectorAll('[data-kc-fsb]');
  if (!bars.length) return null;
  if (!inflight) {
    inflight = fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((r) => r.json())
      .finally(() => setTimeout(() => (inflight = null), 0));
  }
  try {
    const cart = await inflight;
    bars.forEach((bar) => render(bar, cart));
    return cart;
  } catch (e) {
    console.warn('[kc-fsb]', e);
    return null;
  }
}

function initCarousel(addon) {
  if (addon.dataset.kcInit) return;
  addon.dataset.kcInit = '1';
  const list = addon.querySelector('[data-kc-addon-list]');
  const count = addon.querySelector('[data-kc-addon-count]');
  const items = list ? [...list.querySelectorAll('[data-kc-addon-item]')] : [];
  if (!list || items.length < 2) return;
  const idx = () => Math.round(list.scrollLeft / Math.max(1, list.clientWidth));
  const go = (i) => {
    const n = (i + items.length) % items.length;
    list.scrollTo({ left: n * list.clientWidth, behavior: 'smooth' });
  };
  addon.querySelector('[data-kc-addon-prev]')?.addEventListener('click', () => go(idx() - 1));
  addon.querySelector('[data-kc-addon-next]')?.addEventListener('click', () => go(idx() + 1));
  let t;
  list.addEventListener(
    'scroll',
    () => {
      clearTimeout(t);
      t = setTimeout(() => count && (count.textContent = `${idx() + 1} / ${items.length}`), 60);
    },
    { passive: true }
  );
}

async function addOn(btn) {
  if (btn.getAttribute('aria-busy') === 'true') return;
  const id = btn.dataset.variantId;
  if (!id) return;
  const label = btn.textContent.trim() || 'Aggiungi';
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;

  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Impossibile aggiungere');
    const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'kc-addon', itemCount: 1, didError: false },
    });
    document.querySelectorAll('[data-kc-fsb]').forEach((bar) => render(bar, cart));
    setTimeout(refresh, 700);
    btn.textContent = 'Aggiunto';
    setTimeout(() => {
      btn.textContent = label;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }, 1800);
  } catch (e) {
    console.warn('[kc-addon]', e);
    deferred.reject(e);
    btn.textContent = 'Non disponibile';
    setTimeout(() => {
      btn.textContent = label;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }, 2200);
  }
}

if (!window.__kcFsbBound) {
  window.__kcFsbBound = true;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-kc-addon-add]');
    if (btn) {
      e.preventDefault();
      addOn(btn);
    }
  });
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.detail && event.detail.source === 'kc-addon') return;
    const p = event.promise || (event.detail && event.detail.promise);
    const again = () => { refresh(); setTimeout(refresh, 700); };
    if (p && p.then) p.then(again, again);
    else setTimeout(again, 400);
  });
  window.addEventListener('pageshow', (e) => e.persisted && refresh());
  document.addEventListener('theme-drawer:open', () => refresh());
}

document.querySelectorAll('[data-kc-addon]').forEach(initCarousel);
refresh();
